import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  HistoryIcon,
  PackageIcon,
  PercentIcon,
  TagIcon,
  TicketIcon,
  WalletIcon,
} from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Skeleton } from '@/components/skeleton/skeleton';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/tabs/tabs';
import VoucherRedemptionHistory from './voucher-redemption-history';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { getVoucherQueryOptions } from '@/features/vouchers/api/get-voucher.api';
import type { Voucher } from '@/features/vouchers/types/voucher.type';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

function formatDiscount(voucher: Voucher) {
  if (voucher.type === 'PERCENTAGE') return `${voucher.discount}%`;
  return `Rp ${voucher.discount.toLocaleString('id-ID')}`;
}

function isExpired(expiredAt: string) {
  const date = new Date(expiredAt);
  if (Number.isNaN(date.getTime())) return false;
  return date.getTime() < Date.now();
}

export interface DetailVoucherDialogProps extends BaseDialogProps {
  voucherId: string;
}

export default function DetailVoucherDialog({
  dialog,
  voucherId,
}: DetailVoucherDialogProps) {
  const { closeDetailVoucherDialog } = useV2DialogContext();

  const getVoucherQuery = useQuery(
    getVoucherQueryOptions({ voucher_id: voucherId })
  );
  const voucher = getVoucherQuery.data?.result;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailVoucherDialog();
        }
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Detail Voucher</DialogTitle>
          <DialogDescription>Informasi lengkap voucher.</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="detail" className="gap-5">
          <TabsList className="w-full">
            <TabsTrigger value="detail">
              <TicketIcon />
              Detail
            </TabsTrigger>
            <TabsTrigger value="history">
              <HistoryIcon />
              History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="detail">
            {getVoucherQuery.isPending ? (
              <DetailVoucherSkeleton />
            ) : getVoucherQuery.isError || !voucher ? (
              <p className="text-sm text-destructive">
                Gagal memuat data voucher. Silakan coba lagi.
              </p>
            ) : (
              <div className="flex flex-col gap-6">
                <DetailVoucherHeader
                  voucher={voucher}
                  expired={isExpired(voucher.expired_at)}
                />
                <DetailVoucherInfo voucher={voucher} />
                <DetailVoucherMeta voucher={voucher} />
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <VoucherRedemptionHistory voucherId={voucherId} />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

interface DetailVoucherHeaderProps {
  voucher: Voucher;
  expired: boolean;
}

function DetailVoucherHeader({ voucher, expired }: DetailVoucherHeaderProps) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex flex-col gap-1.5">
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <TicketIcon className="size-3" />
          Kode Voucher
        </p>
        <p className="text-2xl font-semibold text-foreground">
          {voucher.voucher_code}
        </p>
      </div>
      <Badge variant={expired ? 'destructive' : 'success'}>
        {expired ? 'Expired' : 'Aktif'}
      </Badge>
    </div>
  );
}

interface DetailVoucherInfoProps {
  voucher: Voucher;
}

function DetailVoucherInfo({ voucher }: DetailVoucherInfoProps) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
      <InfoRow
        icon={<TagIcon className="size-4" />}
        label="Tipe"
        value={voucher.type === 'PERCENTAGE' ? 'Percentage' : 'Flat'}
      />
      <InfoRow
        icon={
          voucher.type === 'PERCENTAGE' ? (
            <PercentIcon className="size-4" />
          ) : (
            <WalletIcon className="size-4" />
          )
        }
        label="Diskon"
        value={formatDiscount(voucher)}
      />
      <InfoRow
        icon={<PackageIcon className="size-4" />}
        label="Stok"
        value={String(voucher.stock)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Kedaluwarsa"
        value={formatDate(voucher.expired_at)}
      />
    </div>
  );
}

interface DetailVoucherMetaProps {
  voucher: Voucher;
}

function DetailVoucherMeta({ voucher }: DetailVoucherMetaProps) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibuat Pada"
        value={formatDate(voucher.created_at)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Diperbarui Pada"
        value={formatDate(voucher.updated_at)}
      />
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </p>
      </div>
    </div>
  );
}

function DetailVoucherSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-7 w-40" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}
